import React from "react"
import PropTypes from "prop-types"
import { optimizeContentfulImage } from "utils/optimizeContentfulImage"
import { getClinicIntro } from "utils/clinicHeadline"
import {
  clinicKey,
  findAsset,
  overridesFor,
  stockHeroFor,
} from "utils/clinicOverrides"
import { NetworkSearchIcon, StethoscopeIcon } from "./badgeIcons"
import "./clinicHero.scss"

// Where the "Make an Appointment" button sends families (the enrollment journey).
const APPOINTMENT_PATH = "/welcomevisit/"

/**
 * TEMPORARY, for previewing on staging. Remove once this copy is in Contentful.
 *
 * Draft hero copy, pinned so the pages can be reviewed at full length. Place
 * names come from each centre's own "Nearby Areas We Serve" list on production,
 * so the geography is accurate. The copy itself is unapproved.
 *
 * To retire an entry: put the paragraph in that clinic's Headline field as the
 * first block above the rule, then delete its line below.
 */

/** Used only where a centre has no intro of its own. */
const HERO_INTRO_FALLBACKS = {
  braintree:
    "Just south of Boston, our Braintree center serves families across the South Shore, including Quincy, Weymouth, Milton, and Randolph. Pediatricians, therapists, and counselors work side by side in one location, so your child's care is coordinated from the very first visit.",
  danvers:
    "On Boston's North Shore, our Danvers center serves families from Salem, Peabody, Beverly, and Marblehead. Your child's doctors and therapists work as one team in a single location, sharing what they learn so progress in one area supports growth in others.",
  dedham:
    "A short drive from Boston, our Dedham center supports families throughout Norfolk County and the surrounding suburbs, including Norwood, Westwood, Needham, and Canton. Medical, developmental, and behavioral care all happen under one roof, so your family is not travelling between providers.",
  "west-springfield":
    "In the Pioneer Valley of Western Massachusetts, our West Springfield center serves families from Springfield, Holyoke, Chicopee, and Agawam. Bringing pediatric medicine, therapy, and counseling together in one place means your child's care plan is built and adjusted by a team who all know them.",
}

/**
 * Deliberate rewrites that replace the copy currently in Contentful.
 *
 * These centres have intros written in the older structure, mostly a single
 * short line about where they are. Each version below keeps every fact from
 * the entry, square footage, landmarks and areas served, and only changes the
 * shape to match the other centres.
 */
const HERO_INTRO_REWRITES = {
  // Entry runs "...change the way you think about comprehensive autism care.
  // This center offers:" and then hands off to a list the hero does not show.
  glendale:
    "Adjacent to Griffith Park, our Glendale center sits between Burbank, Pasadena, and Downtown LA. Across 13,000 square feet, pediatricians, therapists, and counselors work as one team, with three gyms and an enclosed patio for sensorimotor and communication therapies.",

  // Entry's intro stops at the areas served and leaves the rest to a list.
  irvine:
    "In the heart of Orange County, our Irvine center serves families from San Clemente, Huntington Beach, Anaheim, and beyond. At 15,700 square feet, with 30 treatment rooms and an indoor gym, your child's medical care and therapies all happen in one place.",

  // Entry's intro is a single line that hands off to a list of services.
  "westlake-village":
    "Set in the Conejo Valley, our Westlake Village center serves families across Ventura County and the western San Fernando Valley, including Thousand Oaks, Agoura Hills, and Calabasas. At roughly 12,000 square feet, medical care, developmental therapies, and counseling all take place in one location, built around your child rather than around appointments.",

  // Entry's intro ends by pointing at a leadership team further down the page,
  // which is page furniture rather than copy.
  warren:
    "In North Central New Jersey, our Warren center serves families from Newark, Edison, Morristown, and beyond. ABA therapy, medical care, and counseling happen under one roof, so your child is seen by clinicians who all know them and share what they learn.",

  // Entry's intro is two short lines; this keeps both facts in one paragraph.
  "the-woodlands":
    "In the heart of The Woodlands, our center serves the greater Houston area, from Westfield and Atascocita to Conroe and beyond. With both in-center and in-home ABA options, pediatricians, therapists, and counselors work as one team, so your child's care is coordinated from the first visit.",

  trumbull:
    "Located at the tip of Old Mine Park, our Trumbull center serves families across the greater New Haven area, from Huntington to Bridgeport and beyond. Because our pediatricians, therapists, and counselors share one location, your child's care stays joined up as their needs change.",
  shrewsbury:
    "Our Shrewsbury center serves the greater Worcester area and its surrounding towns, including Westborough, Northborough, and Grafton. Doctors, therapists, and counselors collaborate in a single location, giving your family one coordinated team rather than several separate ones.",
  "san-diego":
    "Located in Sorrento Valley near Qualcomm, our San Diego center serves families from La Jolla to La Mesa and beyond. Medical care, developmental therapies, and counseling all happen in one location, so your family is not travelling between providers.",
  plano:
    "Located near Park and Preston in West Plano, our center serves families across the greater Dallas area, from Highland Park to Grapevine and beyond. Pediatricians, therapists, and counselors work as one team in one place, so your child's care is coordinated from the first visit.",
}

/**
 * Several entries end their intro with a lead-in to a list, for example
 * "Our comprehensive services include:". The hero shows the paragraph but not
 * the list, so that clause is left hanging on a colon. Drop it.
 */
const dropDanglingLeadIn = text => {
  const trimmed = (text || "").trim()
  if (!trimmed.endsWith(":")) return trimmed

  const lastSentence = trimmed.lastIndexOf(".")

  return lastSentence === -1 ? trimmed : trimmed.slice(0, lastSentence + 1)
}

const ClinicHero = ({ clinic, overrideAssets }) => {
  // An override wins where a clinic's own hero image is a logo, a stock photo,
  // or a picture of a different centre.
  const { heroImage, prodHero } = overridesFor(clinic?.slug)
  const override = prodHero || findAsset(overrideAssets, heroImage)

  // Last resort, so a centre with no photo of its own still shows something
  // rather than an empty frame.
  const stock = findAsset(overrideAssets, stockHeroFor(clinic?.slug))

  const photoUrl =
    override?.file?.url ||
    clinic?.image?.file?.url ||
    clinic?.gallery?.[0]?.file?.url ||
    stock?.file?.url
  const intro = dropDanglingLeadIn(
    HERO_INTRO_REWRITES[clinicKey(clinic?.slug)] ||
      getClinicIntro(clinic?.headline) ||
      HERO_INTRO_FALLBACKS[clinicKey(clinic?.slug)] ||
      ""
  )

  return (
    <section className={`ClinicHero${photoUrl ? "" : " ClinicHero--no-photo"}`}>
      <div className="container-v2 ClinicHero__inner">
        <div className="ClinicHero__media">
          {photoUrl && (
            <img
              alt={
                clinic?.name
                  ? `Cortica ${clinic.name} center`
                  : "Cortica center"
              }
              className="ClinicHero__photo"
              // Crop to a fixed landscape frame. Several centres have portrait
              // photos, which previously rendered as a tall column in the hero.
              src={optimizeContentfulImage(photoUrl, {
                fit: "fill",
                height: 600,
                quality: 85,
                width: 900,
              })}
            />
          )}
          {/* Decorative Cortica badge "stickers" */}
          <span
            aria-hidden="true"
            className="ClinicHero__badge ClinicHero__badge--pin"
          >
            <StethoscopeIcon />
          </span>
          <span
            aria-hidden="true"
            className="ClinicHero__badge ClinicHero__badge--search"
          >
            <NetworkSearchIcon />
          </span>
        </div>

        <div className="ClinicHero__content">
          <h1 className="ClinicHero__title">
            ABA Therapy &amp; Autism Services in {clinic?.name}
          </h1>
          {intro && <p className="ClinicHero__subtitle">{intro}</p>}
          <a className="ClinicHero__cta" href={APPOINTMENT_PATH}>
            Make an Appointment
            <svg
              aria-hidden="true"
              className="ClinicHero__cta-arrow"
              fill="none"
              height="18"
              viewBox="0 0 24 24"
              width="18"
            >
              <path
                d="M5 12h14M13 6l6 6-6 6"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  )
}

ClinicHero.propTypes = {
  clinic: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.shape({
      file: PropTypes.shape({ url: PropTypes.string }),
    }),
    gallery: PropTypes.arrayOf(
      PropTypes.shape({ file: PropTypes.shape({ url: PropTypes.string }) })
    ),
    headline: PropTypes.shape({ json: PropTypes.object }),
    slug: PropTypes.string,
  }),
  overrideAssets: PropTypes.array,
}

export default ClinicHero
